import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { EnvironmentEntry } from "@visioni/a3s-environments";
import { environment } from "./runtime";
import { useAuthStore } from "@src/features/auth/auth-store";
import { clearLastUsedTenant } from "@src/features/tenants";

interface EnvironmentState {
  env: EnvironmentEntry;
  /** Set when user confirms env switch; consumed by login screen to avoid crash during clear (see switchEnvironment). */
  pendingEnvironmentSwitch: EnvironmentEntry | null;
  setEnvironment: (env: EnvironmentEntry) => void;
  setPendingEnvironmentSwitch: (entry: EnvironmentEntry | null) => void;
  resetEnvironment: () => void;
}

const store = create<EnvironmentState>()(
  persist(
    (set) => ({
      env: environment(),
      pendingEnvironmentSwitch: null,
      setEnvironment: (env) => set({ env }),
      setPendingEnvironmentSwitch: (entry) => set({ pendingEnvironmentSwitch: entry }),
      resetEnvironment: () => set({ env: environment() }),
    }),
    {
      name: "environment-store",
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({ env: state.env }),
    },
  ),
);

/** React hook to access current environment */
export function useEnvironment(): EnvironmentEntry {
  return store((s) => s.env);
}

/** Async getter - waits for hydration from storage */
export async function getEnvironment(): Promise<EnvironmentEntry> {
  await store.persist.rehydrate();
  return store.getState().env;
}

/** Async setter - waits for hydration then sets environment */
export async function setEnvironment(env: EnvironmentEntry): Promise<void> {
  await store.persist.rehydrate();
  store.getState().setEnvironment(env);
}

/** Get/set pending env switch (used to defer switch until after navigation to login, avoiding RN crash). */
export function getPendingEnvironmentSwitch(): EnvironmentEntry | null {
  return store.getState().pendingEnvironmentSwitch;
}

export function setPendingEnvironmentSwitch(entry: EnvironmentEntry | null): void {
  store.getState().setPendingEnvironmentSwitch(entry);
}

/** Switch to a different environment, clearing auth and tenant data */
export async function switchEnvironment(env: EnvironmentEntry): Promise<void> {
  const currentEnv = await getEnvironment();
  if (currentEnv.fqdn === env.fqdn) return;

  await useAuthStore.getState().clearAuth();
  await clearLastUsedTenant();
  await setEnvironment(env);
}

/** Reset to the default build-time environment, clearing auth and tenant data */
export async function resetEnvironment(): Promise<void> {
  const currentEnv = await getEnvironment();
  const defaultEnv = environment();
  if (currentEnv.fqdn === defaultEnv.fqdn) return;

  await useAuthStore.getState().clearAuth();
  await clearLastUsedTenant();
  await store.persist.rehydrate();
  store.getState().resetEnvironment();
}
